package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.features.auth.AuthPhase
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.workspace.InviteAcceptRequestDto
import co.maytes.standupbot.shared.dto.workspace.InviteAcceptResponseDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window

@Page("/invite/accept")
@Composable
fun InviteAcceptPage() {
    val ctx = rememberPageContext()
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val ui = rememberUiStore()
    val token = (ctx.route.params["token"] ?: ctx.route.params["link"]).orEmpty()

    var status by remember { mutableStateOf<ClaimStatus>(ClaimStatus.Idle) }

    LaunchedEffect(token, auth.phase) {
        if (status != ClaimStatus.Idle) return@LaunchedEffect
        when {
            token.isBlank() ->
                status = ClaimStatus.Error("Missing invite token. Ask the inviter to resend.")
            auth.phase is AuthPhase.Loading -> Unit
            auth.phase is AuthPhase.Unauthenticated -> {
                status = ClaimStatus.Redirecting
                window.location.href = "/auth/slack/start?returnTo=" + encodeUriComponent("/app") + "&invite=" + encodeUriComponent(token)
            }
            else -> {
                status = ClaimStatus.Submitting
                when (
                    val r = api.post<InviteAcceptRequestDto, InviteAcceptResponseDto>(
                        path = "/api/v1/invites/accept",
                        body = InviteAcceptRequestDto(token = token),
                    )
                ) {
                    is Result.Ok -> {
                        val message = if (r.value.outcome == "ALREADY_HAS_ACCESS") {
                            "You already have access to ${r.value.workspaceName}"
                        } else {
                            "Joined ${r.value.workspaceName}"
                        }
                        ui.showToast(message, ToastKind.Success)
                        auth.bootstrap()
                        ctx.router.navigateTo("/app")
                    }
                    is Result.Err -> status = ClaimStatus.Error(r.error.displayMessage)
                }
            }
        }
    }

    Box(
        modifier = Modifier.fillMaxSize().padding(Spacing.xxl),
        contentAlignment = Alignment.Center,
    ) {
        when (val s = status) {
            ClaimStatus.Idle, ClaimStatus.Submitting, ClaimStatus.Redirecting ->
                SpanText(
                    text = if (s == ClaimStatus.Redirecting) "Taking you to Slack to sign in…" else "Accepting invite…",
                    modifier = Modifier.fontSize(Typography.body.size),
                )
            is ClaimStatus.Error ->
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    SpanText(
                        text = s.message,
                        modifier = Modifier.fontSize(Typography.body.size),
                    )
                    Box(modifier = Modifier.padding(top = Spacing.md))
                    Button(label = "Back to home", onClick = { ctx.router.navigateTo("/") })
                }
        }
    }
}

private sealed interface ClaimStatus {
    data object Idle : ClaimStatus
    data object Submitting : ClaimStatus
    data object Redirecting : ClaimStatus
    data class Error(val message: String) : ClaimStatus
}

private fun encodeUriComponent(value: String): String =
    js("encodeURIComponent(value)").unsafeCast<String>()
